/**
 * @fileoverview Pure helpers for sending chat messages over WebSocket.
 * @module pages/chat/chatSendMessage
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { FILE_UPLOAD_TIMEOUT_MS } from "../../utils/chatUiConstants.js";

/** Resolves the other participant's user id for the active chat. */
export const getChatRecipientUserId = (selectedChat, userId) =>
  selectedChat.userAId === userId
    ? selectedChat.userBId
    : selectedChat.userAId;

/** Builds the WebSocket payload for a chat message. */
export const buildChatMessagePayload = ({
  message,
  chatId,
  senderUserId,
  recipientUserId,
  isFile,
  messageId,
}) => ({
  action: "sendMessage",
  message,
  chatId,
  senderUserId,
  recipientUserId,
  isFile,
  messageId,
});

/** Sends a trimmed text message and updates optimistic UI state. */
export const sendTextChatMessage = (ctx, userId) => {
  const textToSend = ctx.messageInput.trim();
  if (!textToSend) {
    return;
  }

  const messageId = Date.now().toString();
  const recipientUserId = getChatRecipientUserId(ctx.selectedChat, userId);

  ctx.ws.current.send(
    JSON.stringify(
      buildChatMessagePayload({
        message: textToSend,
        chatId: ctx.selectedChat.PK,
        senderUserId: userId,
        recipientUserId,
        isFile: false,
        messageId,
      }),
    ),
  );

  ctx.setChatMessages((prev) => [
    ...prev,
    {
      type: "NEW_MESSAGE",
      chatId: ctx.selectedChat.PK,
      messageId,
      message: textToSend,
      senderUserId: userId,
      timestamp: Date.now().toString(),
    },
  ]);

  ctx.setChats((prevChats) =>
    prevChats.map((chat) =>
      chat.PK === ctx.selectedChat.PK
        ? { ...chat, lastMessage: textToSend }
        : chat,
    ),
  );
  ctx.setMessageInput("");
};

/** Sends a file message and tracks the pending upload timeout. */
export const sendFileChatMessage = (ctx, userId) => {
  if (!ctx.selectedFile) {
    return;
  }

  ctx.setIsFileUploading(true);
  const fileName = ctx.selectedFile.name;
  const messageId = Date.now().toString();
  ctx.pendingFileUploads.current.set(messageId, ctx.selectedFile);

  const recipientUserId = getChatRecipientUserId(ctx.selectedChat, userId);
  ctx.ws.current.send(
    JSON.stringify(
      buildChatMessagePayload({
        message: fileName,
        chatId: ctx.selectedChat.PK,
        senderUserId: userId,
        recipientUserId,
        isFile: true,
        messageId,
      }),
    ),
  );

  ctx.setSelectedFile(null);
  ctx.setShowFileUpload(false);
  ctx.setFileError("");

  setTimeout(() => {
    if (ctx.pendingFileUploads.current.has(messageId)) {
      logger.warn("File upload timeout - no response received");
      ctx.pendingFileUploads.current.delete(messageId);
      ctx.setIsFileUploading(false);
    }
  }, FILE_UPLOAD_TIMEOUT_MS);
};

/** Sends pending text and/or file messages when the socket is open. */
export const sendChatMessages = async (ctx) => {
  if ((!ctx.messageInput.trim() && !ctx.selectedFile) || !ctx.selectedChat) {
    return;
  }

  try {
    if (ctx.ws.current?.readyState !== WebSocket.OPEN) {
      logger.error("WebSocket is not open");
      return;
    }

    const userId = localStorage.getItem("userId");
    sendTextChatMessage(ctx, userId);
    sendFileChatMessage(ctx, userId);
  } catch (error) {
    logger.error("Error sending message:", { err: sanitizeError(error) });
    ctx.setIsFileUploading(false);
  }
};
